import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import Button from '../../components/Button';
import { useCrudApi } from '../../hooks/useApi';
import apiService from '../../services/apiService';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, User } from 'lucide-react';

interface Technician {
  _id: string;
  firstName: string;
  lastName: string;
  departement?: string;
}

const AssignRequestPage: React.FC = () => {
  const { id: requestId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [selectedTechnician, setSelectedTechnician] = useState<string>('');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all');
  const [isAssigning, setIsAssigning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [departments, setDepartments] = useState<string[]>([]);

  const { 
    items: allTechnicians,
    isLoading: isLoadingTechnicians,
    fetchItems: fetchAllTechnicians,
  } = useCrudApi<Technician, any, any>(apiService.users);

  useEffect(() => {
    fetchAllTechnicians({ role: 'technician' });
    setSelectedTechnician('');
    setSelectedDepartment('all');
    setError(null);
  }, [fetchAllTechnicians]);

  useEffect(() => {
    if (allTechnicians.length > 0) {
      const uniqueDepartments = Array.from(new Set(allTechnicians.map(tech => tech.departement).filter(Boolean))) as string[];
      setDepartments(['all', ...uniqueDepartments]);
    }
  }, [allTechnicians]);

  const techniciansToDisplay = React.useMemo(() => {
    if (selectedDepartment === 'all') {
      return allTechnicians;
    } else {
      return allTechnicians.filter(tech => tech.departement === selectedDepartment);
    }
  }, [allTechnicians, selectedDepartment]);

  const handleAssign = async () => {
    if (!requestId || !selectedTechnician) {
      setError('Veuillez sélectionner un technicien.');
      return;
    }

    setIsAssigning(true);
    setError(null);
    try {
      await apiService.requests.update(requestId, { assignedTechnician: selectedTechnician, status: 'assigned' });
      toast.success('Demande assignée avec succès !');
      navigate('/admin/requests'); // Navigate back to requests list
    } catch (err) {
      console.error('Failed to assign technician:', err);
      toast.error('Échec de l\'assignation du technicien. Veuillez réessayer.');
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <div>
            <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4 flex items-center text-primary-600">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Retour aux Demandes
            </Button>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <User className="w-8 h-8 mr-3 text-primary-600" />
              Assigner un Technicien à la Demande #{requestId}
            </h1>
            <p className="text-gray-600 mt-2">
              Sélectionnez un technicien et son département pour assigner cette demande.
            </p>
          </div>
          <Button onClick={handleAssign} disabled={isAssigning || !selectedTechnician}>
            {isAssigning ? 'Assignation en cours...' : 'Assigner'}
          </Button>
        </div>

        <div className="space-y-4">
          <p className="text-gray-700">Sélectionnez un technicien à assigner à cette demande.</p>
          {isLoadingTechnicians ? (
            <p>Chargement des techniciens...</p>
          ) : (
            <>
              <label htmlFor="department-select" className="block text-sm font-medium text-gray-700">Filtrer par Département</label>
              <select
                id="department-select"
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
              >
                {departments.map(dept => (
                  <option key={dept} value={dept}>
                    {dept === 'all' ? 'Tous les départements' : dept}
                  </option>
                ))}
              </select>

              <label htmlFor="technician-select" className="block text-sm font-medium text-gray-700">Sélectionner un Technicien</label>
              <select
                id="technician-select"
                value={selectedTechnician}
                onChange={(e) => setSelectedTechnician(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
              >
                <option value="">-- Sélectionner un technicien --</option>
                {techniciansToDisplay.map((tech) => (
                  <option key={tech._id} value={tech._id}>
                    {tech.firstName} {tech.lastName} {tech.departement ? `(${tech.departement})` : ''}
                  </option>
                ))}
              </select>
            </>
          )}
          {error && <p className="text-error-600 text-sm">{error}</p>}
        </div>
      </div>
    </Layout>
  );
};

export default AssignRequestPage;
